export const MULTI_VALUE_SEPARATOR = ', ';

/** Placeholder shown for required fields that have no value. Single source of truth for chip styling and note template logic. */
export const REQUIRED_EMPTY_PLACEHOLDER = '! Required';

/** Escape for comma-space inside a value (so display can show ", "). */
export const VALUE_COMMA_PLACEHOLDER = '\u001E';

/**
 * Separator between multiple selected values in stored form. Distinct from
 * VALUE_COMMA_PLACEHOLDER so that multi-select "A" + "B" is stored as "A\u001DB"
 * and never equals a single option "A, B".
 */
const INTERNAL_MULTI_SEPARATOR = '\u001D';

/**
 * Converts internal stored value to display/persisted form. Replaces both
 * INTERNAL_MULTI_SEPARATOR and VALUE_COMMA_PLACEHOLDER with ", ".
 */
export const valueForDisplay = (stored: string): string =>
  stored
    .replaceAll(INTERNAL_MULTI_SEPARATOR, MULTI_VALUE_SEPARATOR)
    .replaceAll(VALUE_COMMA_PLACEHOLDER, MULTI_VALUE_SEPARATOR);

/**
 * Parses currentValue into a set of selected values.
 * - If the string contains INTERNAL_MULTI_SEPARATOR, it was produced by
 *   formatMultiValue (multi-select); split by it and unescape comma placeholders.
 * - Otherwise (e.g. API defaultValue or legacy): if allValues contains the
 *   whole string, treat as single option; else parse using allValues so that
 *   option labels that contain ", " (e.g. "General, Routine") are matched
 *   correctly and the set only contains strings that exist in allValues (so
 *   dropdown checkboxes match after reopen).
 */
export const parseMultiValue = (
  currentValue: string,
  allValues?: string[],
): Set<string> => {
  const s = currentValue.trim();
  if (!s) return new Set<string>();
  if (s.includes(INTERNAL_MULTI_SEPARATOR)) {
    const selected = new Set<string>();
    for (const segment of s.split(INTERNAL_MULTI_SEPARATOR)) {
      const unescaped = segment
        .trim()
        .replaceAll(VALUE_COMMA_PLACEHOLDER, MULTI_VALUE_SEPARATOR);
      if (unescaped) selected.add(unescaped);
    }
    return selected;
  }
  if (allValues?.length && allValues.includes(s)) return new Set([s]);

  const selected = new Set<string>();
  if (allValues?.length) {
    // Match display string against option labels so options containing ", "
    // (e.g. "General, Routine") are recognized and the set only contains
    // exact allValues entries.
    const sorted = [...allValues].sort((a, b) => b.length - a.length);
    let remaining = s.replaceAll(
      VALUE_COMMA_PLACEHOLDER,
      MULTI_VALUE_SEPARATOR,
    );
    while (remaining.length > 0) {
      const trimmed = remaining.trimStart();
      if (!trimmed) break;
      let matched = false;
      for (const opt of sorted) {
        if (!opt || !trimmed.startsWith(opt)) continue;
        const after = trimmed.slice(opt.length);
        const atEnd = after.length === 0;
        const followedBySep =
          after.startsWith(MULTI_VALUE_SEPARATOR) || after.startsWith(',');
        if (atEnd || followedBySep) {
          selected.add(opt);
          remaining = after.startsWith(MULTI_VALUE_SEPARATOR)
            ? after.slice(MULTI_VALUE_SEPARATOR.length)
            : after.startsWith(',')
              ? after.slice(1).trimStart()
              : after;
          matched = true;
          break;
        }
      }
      if (!matched) {
        const idx = trimmed.indexOf(MULTI_VALUE_SEPARATOR);
        const segment = (idx >= 0 ? trimmed.slice(0, idx) : trimmed).trim();
        if (segment && allValues.includes(segment)) selected.add(segment);
        remaining =
          idx >= 0 ? trimmed.slice(idx + MULTI_VALUE_SEPARATOR.length) : '';
      }
    }
  } else {
    const segments = s.split(MULTI_VALUE_SEPARATOR);
    for (const segment of segments) {
      const unescaped = segment
        .trim()
        .replaceAll(VALUE_COMMA_PLACEHOLDER, MULTI_VALUE_SEPARATOR);
      if (unescaped) selected.add(unescaped);
    }
  }
  return selected;
};

/**
 * Joins selected values with INTERNAL_MULTI_SEPARATOR so the result is never
 * equal to a single option that looks like "A, B". Comma-space inside each
 * value is replaced with VALUE_COMMA_PLACEHOLDER.
 * When orderBy is provided, selected values are emitted in that order so
 * deselect/reselect does not change the string (avoids false "changed" in diffs).
 */
export const formatMultiValue = (
  selected: Set<string>,
  orderBy?: string[],
): string => {
  const escape = (v: string) =>
    v.replaceAll(MULTI_VALUE_SEPARATOR, VALUE_COMMA_PLACEHOLDER);
  if (orderBy?.length) {
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const v of orderBy) {
      if (selected.has(v)) {
        ordered.push(escape(v));
        seen.add(v);
      }
    }
    for (const v of selected) {
      if (!seen.has(v)) ordered.push(escape(v));
    }
    return ordered.join(INTERNAL_MULTI_SEPARATOR);
  }
  return Array.from(selected).map(escape).join(INTERNAL_MULTI_SEPARATOR);
};

/**
 * Normalizes a display-format multi-value string to a canonical form (sorted)
 * so that "A, B" and "B, A" compare equal and do not trigger unnecessary saves.
 */
export const normalizeMultiValueDisplay = (display: string): string =>
  display
    .split(MULTI_VALUE_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(MULTI_VALUE_SEPARATOR);
